<template>
  <div class="elevation-3 widget-list white rounded-lg pb-1">
    <header class="widget-list__header rounded-t-lg pa-8 primary">
      <div class="widget-list__header-description d-flex justify-space-between">
        <h3
          v-if="headingText"
          class="text-h3"
        >
          {{ widgetListHeadingText }}
        </h3>
        <pp-button
          type="secondary"
          class="widget-list__header__btn-close text-h3 white--text"
          aria-label="Close"
          icon="close"
          @click="hideWidgetList"
        />
      </div>
      <h6
        v-if="subHeadingText"
        class="text-h6"
      >
        {{ widgetListSubHeadingText }}
      </h6>
    </header>
    <div class="widget-list__body elevation-3 white rounded-lg ">
      <multi-location-call-us v-show="showMultiLocationCallUs" />
      <ul
        v-show="!showMultiLocationCallUs"
        class="pa-6"
      >
        <template
          v-for="widget in formattedWidgets"
        >
          <a
            v-if="widget.isALink"
            :key="widget.label"
            :href="widget.linkUrl"
            :target="widget.isTargetBlank && '_blank'"
            class="text-decoration-none"
          >
            <widget-list-item
              :icon="widget.icon"
              :label="widget.label"
              @widgetClick="handleWidgetClick(widget)"
            />
          </a>
          <widget-list-item
            v-else
            :key="widget.id"
            :icon="widget.icon"
            :label="widget.label"
            @widgetClick="handleWidgetClick(widget)"
          />
        </template>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex'
import { CHANNEL_TYPES, WIDGET_EVENT, WIDGET_CLICKED_SESSION_KEY, MULTICHANNEL_LOCATIONS_HEADING_TEXT } from '@/constants'
import WidgetListItem from '@/components/widget-list-item/WidgetListItem'
import MultiLocationCallUs from '@/components/multi-location-call-us/MultiLocationCallUs'

export default {
  name: 'Widget',
  components: {
    'widget-list-item': WidgetListItem,
    'multi-location-call-us': MultiLocationCallUs
  },
  data () {
    return {
      showMultiLocationCallUs: false
    }
  },
  computed: {
    ...mapState({
      widgets: 'widgets'
    }),
    ...mapGetters({
      headingText: 'headingText',
      subHeadingText: 'subHeadingText',
      customLinkUrl: 'customLinkUrl',
      customLinkLabel: 'customLinkLabel',
      callTrackingNumber: 'callTrackingNumber',
      widgets: 'widgets',
      backgroundColor: 'backgroundColor',
      textColor: 'textColor',
      isWidgetOpen: 'isWidgetOpen',
      practiceLocations: 'practiceLocations',
      isMobileSafari: 'isMobileSafari'
    }),
    formattedWidgets () {
      return this.widgets
        .filter((widget) => {
          if (widget.type === CHANNEL_TYPES.PHONE && !this.practiceLocations.length) {
            return false
          }
          return true
        })
        .map((widget) => {
          if (widget.type === CHANNEL_TYPES.LINK) {
            widget.label = this.customLinkLabel
            widget.linkUrl = this.customLinkUrl
            widget.isALink = true
            widget.isTargetBlank = true
          } else if (widget.type === CHANNEL_TYPES.PHONE) {
            if (this.practiceLocations.length > 1) {
              widget.isALink = false
              widget.isTargetBlank = false
            } else if (this.practiceLocations.length === 1) {
              widget.label = this.practiceLocations[0].callTrackingNumber
              widget.linkUrl = 'tel:' + this.practiceLocations[0].callTrackingNumber
              widget.isALink = true
              widget.isTargetBlank = this.isMobileSafari
            }
          }
          return widget
        })
    },
    widgetListHeadingText () {
      return this.showMultiLocationCallUs ? MULTICHANNEL_LOCATIONS_HEADING_TEXT.HEADING : this.headingText
    },
    widgetListSubHeadingText () {
      return this.showMultiLocationCallUs ? MULTICHANNEL_LOCATIONS_HEADING_TEXT.SUB_HEADING : this.subHeadingText
    }
  },
  methods: {
    ...mapMutations({
      setIsWidgetOpen: 'SET_IS_WIDGET_OPEN'
    }),
    trackWidgetClick (widget) {
      this.$ppAnalytics.trackEvent({
        action: 'multi_channel_item_click',
        label: widget.id
      })
    },
    trackHideWidgetList () {
      this.$ppAnalytics.trackEvent({
        action: 'multi_channel_list_close'
      })
    },
    handleWidgetClick (widget) {
      this.trackWidgetClick(widget)
      const { type, widgetId, renderType } = widget
      const payload = {
        widgetId,
        renderType,
        backgroundColor: this.backgroundColor,
        textColor: this.textColor
      }

      switch (type) {
        case CHANNEL_TYPES.WIDGET:
          window.parent.postMessage(
            { type: WIDGET_EVENT.OPEN_WIDGET, payload },
            { targetOrigin: this.$originHost }
          )
          this.setIsWidgetOpen(false)
          break
        case CHANNEL_TYPES.PHONE:
          if (this.practiceLocations.length > 1) {
            this.showMultiLocationCallUs = true
          }
          break
      }
    },
    hideWidgetList () {
      sessionStorage.setItem(WIDGET_CLICKED_SESSION_KEY, true)
      this.trackHideWidgetList()
      this.setIsWidgetOpen(false)
      this.showMultiLocationCallUs = false
    }
  }
}
</script>
<style lang="scss" scoped>
  $header-height: 162px;
  $padding-spacing: 64px;
  $widget-body-padding: 2em;
  $widget-body-height: 120px;
  $widget-body-position: 100px;

  .widget-list {
    width: 430px;

    @include mobile() {
      display: flex;
      flex-direction: column;
      width: var(--pp-parent-width, 100vw);
    }

    &__header {
      color: var(--pp-primary-button-text-color, #{$grey-60});
      height: $header-height;

      @include mobile() {
        width: var(--pp-parent-width, 100vw);
      }

      &__btn-close {
        background-color: var(--pp-theme-color);
        border: none;
        right: -16px;
        top: -16px;
      }
    }

    &__body {
      margin: -42px $widget-body-padding $widget-body-padding $widget-body-padding;

      ul {
        list-style-type: none;
      }
    }
  }
</style>
